import React, { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import {
    buscarOrganizadoresConfiguracion,
    detallesOrganizadorConfiguracion,
    buscarUbicacionesConfiguracion
} from '../../../actions/configuracion';

import NuevaUbicacion from './NuevaUbicacion';
import EditarUbicacion from './EditarUbicacion';
import EliminarUbicacion from './EliminarUbicacion';

const Ubicaciones = () => {

    const { id } = useParams();       // id del organizador en la URL
    const idOrganizador = id;

    const dispatch = useDispatch();

    const { listado: listadoOrganizadores, detalle: detalleOrganizador, areaActual } =
        useSelector(state => state.configuracion.organizadores);

    const { listado: listadoUbicaciones } =
        useSelector(state => state.configuracion.ubicaciones);

    const [ubicaciones, setUbicaciones] = useState([]);
    const [filterText, setFilterText] = useState('');

    const [ubicacionAgregar, setUbicacionAgregar] = useState(null);
    const [ubicacionEditar, setUbicacionEditar] = useState(null);
    const [ubicacionEliminar, setUbicacionEliminar] = useState(null);

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    // Cargar organizador y área para el breadcrumb y el select
    useEffect(() => {
        (async () => {
            const resp = await dispatch(detallesOrganizadorConfiguracion(idOrganizador));
            if (resp && resp.success && resp.organizador && resp.organizador.area) {
                const idArea = resp.organizador.area;
                // Si no tenemos cargados los organizadores de esa área, los pedimos
                if (!areaActual || areaActual !== idArea) {
                    dispatch(buscarOrganizadoresConfiguracion(idArea));
                }
            }
        })();

        dispatch(buscarUbicacionesConfiguracion(idOrganizador));
    }, [dispatch, idOrganizador]);

    useEffect(() => {
        setUbicaciones(listadoUbicaciones || []);
    }, [listadoUbicaciones]);

    const organizadorActual = useMemo(() => {
        if (detalleOrganizador && detalleOrganizador._id === idOrganizador) {
            return detalleOrganizador;
        }
        return listadoOrganizadores.find(o => o._id === idOrganizador) || null;
    }, [detalleOrganizador, listadoOrganizadores, idOrganizador]);

    const nombreOrganizador = organizadorActual ? organizadorActual.nombre : 'Organizador';

    // Breadcrumb
    useEffect(() => {
        const rutasBread = [
            {
                activo: false,
                nombre: 'Áreas',
                path: '/configuracion/areas'
            },
            {
                activo: false,
                nombre: 'Organizadores',
                path: areaActual
                    ? `/configuracion/areas/organizadores/${areaActual}`
                    : '/configuracion/areas'
            },
            {
                activo: true,
                nombre: `Ubicaciones - ${nombreOrganizador}`,
                path: `/configuracion/areas/organizadores/ubicaciones/${idOrganizador}`
            }
        ];
        dispatch(editarBreadcrumb(rutasBread));
    }, [dispatch, nombreOrganizador, idOrganizador, areaActual]);

    // Handlers modales
    const handleAgregarUbicacion = (u) => {
        setUbicacionEditar(null);
        setUbicacionEliminar(null);
        setUbicacionAgregar(u);
    };

    const handleEditarUbicacion = (u) => {
        setUbicacionAgregar(null);
        setUbicacionEliminar(null);
        setUbicacionEditar(u);
    };

    const handleEliminarUbicacion = (u) => {
        setUbicacionAgregar(null);
        setUbicacionEditar(null);
        setUbicacionEliminar(u);
    };

    const columns = [
        {
            name: 'Organizador',
            selector: () => nombreOrganizador,
            wrap: true
        },
        {
            name: 'Nombre',
            selector: row => row.nombre,
            wrap: true
        },
        {
            name: 'Productos',
            selector: row => row.productos,
            center: true,
            width: '150px'
        },
        {
            name: '',
            cell: row => (
                <div className="text-right w-100">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2 float-end"
                        onClick={() => handleEliminarUbicacion(row)}
                    >
                        <i className="fas fa-trash text-danger"></i>
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2 float-end"
                        onClick={() => handleEditarUbicacion(row)}
                    >
                        <i className="fas fa-edit text-warning"></i>
                    </button>
                </div>
            )
        }
    ];

    const filteredItems = ubicaciones.filter(
        u =>
            (u.nombre || '').toLowerCase().includes(filterText.toLowerCase())
    );

    const subHeaderComponent = useMemo(() => (
        <div className="d-flex w-100 justify-content-between">
            <div className="col-md-4">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por nombre..."
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                />
            </div>

            <div className="d-flex justify-content-end">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleAgregarUbicacion({})}
                >
                    Agregar ubicación
                </button>
            </div>
        </div>
    ), [filterText]);

    return (
        <Fragment>
            <NuevaUbicacion
                ubicacion={ubicacionAgregar}
                reestablecerUbicacion={handleAgregarUbicacion}
                idOrganizadorVista={idOrganizador}
            />
            <EditarUbicacion
                ubicacion={ubicacionEditar}
                reestablecerUbicacion={handleEditarUbicacion}
                idOrganizadorVista={idOrganizador}
            />
            <EliminarUbicacion
                ubicacion={ubicacionEliminar}
                reestablecerUbicacion={handleEliminarUbicacion}
            />

            <div className="row">
                <div className="col-md-12 px-4 pb-4">
                    <h3 className="mb-1 mt-5">Ubicaciones</h3>
                    <p className="mb-3">
                        Organizador: <strong>{nombreOrganizador}</strong>
                    </p>

                    <div className="mt-2">
                        <DataTable
                            columns={columns}
                            data={filteredItems}
                            pagination
                            paginationPerPage={20}
                            paginationRowsPerPageOptions={[5, 10, 20, 50]}
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover={true}
                            subHeader
                            subHeaderComponent={subHeaderComponent}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default Ubicaciones;
